/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

/**
 * Subscription plan types (enum values from backend)
 */
export type SubscriptionPlanType = 'free' | 'premium'

/**
 * Subscription status types (enum values from backend)
 */
export type SubscriptionStatusType = 'active' | 'cancelled' | 'expired'

/**
 * Subscription plan for Stripe checkout
 */
export type SubscriptionPlan = 'monthly' | 'yearly'

/**
 * User subscription state with enum-based types
 */
export interface UserSubscriptionState {
  plan: SubscriptionPlanType
  status?: SubscriptionStatusType
  usageCount: number
  maxFreeUsage: number
  isPremium: boolean
  isCancelled?: boolean
  isExpired?: boolean
  expiresAt?: Date | null
  cancelledAt?: Date | null
  startedAt?: Date | null
}

/**
 * Subscription plan definition for UI
 */
export interface SubscriptionPlanDefinition {
  id: string
  name: string
  price: number
  currency: string
  interval: 'month' | 'year'
  features: string[]
  savings?: string | null
  isRecommended?: boolean
}

/**
 * Backend subscription response (matches the C# SubscriptionResponse model)
 */
export interface SubscriptionResponse {
  success: boolean
  plan?: SubscriptionPlanType | null
  usageCount?: number
  maxFreeUsage?: number
  error?: string | null
}

/**
 * Backend cancellation response (matches the C# CancelSubscriptionResponse model)
 */
export interface CancelSubscriptionResponse {
  success: boolean
  expiresAt?: string | null
  message?: string | null
  error?: string | null
}

/**
 * Checkout session creation result
 */
export interface CheckoutSessionResult {
  success: boolean
  url?: string
  sessionId?: string
  error?: string
}

/**
 * Subscription cancellation result
 */
export interface CancellationResult {
  success: boolean
  expiresAt?: Date
  message?: string
  error?: string
}

/**
 * Type guard to check if a plan is premium
 */
export function isPremiumPlan(plan: SubscriptionPlanType): boolean {
  return plan === 'premium'
}

/**
 * Type guard to check if a status is active
 */
export function isActiveStatus(status: SubscriptionStatusType): boolean {
  return status === 'active'
}

/**
 * Type guard to check if a status is cancelled
 */
export function isCancelledStatus(status: SubscriptionStatusType): boolean {
  return status === 'cancelled'
}

/**
 * Type guard to check if a status is expired
 */
export function isExpiredStatus(status: SubscriptionStatusType): boolean {
  return status === 'expired'
}

/**
 * Convert backend subscription response to frontend state
 */
export function mapSubscriptionResponse(response: SubscriptionResponse): UserSubscriptionState {
  const plan = response.plan || 'free'
  
  return {
    plan,
    status: 'active', // Default status, can be updated from other sources
    usageCount: response.usageCount || 0,
    maxFreeUsage: response.maxFreeUsage || 5,
    isPremium: isPremiumPlan(plan),
    isCancelled: false,
    isExpired: false,
    expiresAt: null,
    cancelledAt: null,
    startedAt: null
  }
}
